import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { Field, Formik } from "formik";
import { observer } from "mobx-react-lite";
import React from "react";
import { boolean, object, string, array } from "yup";
import { EnhancedTextarea } from "../components/form-elements/ExpandingTextArea";
import { scrollToTop } from "../utils/functions";
import _specialNeedsData from "./formData/special-needs.json";

const ToggleCheckbox: React.FC<{
  color?: string;
  label?: string;
  value: Array<any>;
  onChange: (param: any) => void;
  placeholder?: string;
  [key: string]: any;
}> = observer(
  ({
    label,
    color,
    value: defaultValue = [],
    onChange = () => {},
    placeholder,
    ...rest
  }) => {
    return (
      <Box {...rest}>
        {label && <FormLabel mb={3}>{label}</FormLabel>}
        <CheckboxGroup
          size="lg"
          value={defaultValue}
          onChange={(value) => onChange(value)}
        >
          <Grid templateColumns="repeat(2, 1fr)" gap={5}>
            {_specialNeedsData.specialNeeds.map((option, idx) => {
              return (
                <Checkbox borderColor="neutral.500" value={option} key={option}>
                  <Text fontSize="md">{option}</Text>
                </Checkbox>
              );
            })}
          </Grid>
        </CheckboxGroup>
      </Box>
    );
  },
);

export type SpecialNeedsProps = {
  loading: boolean;
  onSubmit: (...params: any[]) => Promise<any>;
  specialNeedsData?: any;
  specialNeeds: {
    disabilitiesList: string[];
    isSpecialNeedsCertified: boolean;
    specialNeedsExperience: string;
  };
};

const commonProps = ({ name }: any, { touched, errors }: any) => {
  const defaults = {
    isInvalid: Boolean(touched[name] && errors[name]),
    errorMessage: errors[name],
  };
  return {
    disabilitiesList: {
      ...defaults,
    },
    isSpecialNeedsCertified: {},
    specialNeedsExperience: {
      ...defaults,
      placeholder:
        "Tell us your qualification, experience and track record in teaching learners living with special needs",
      characterLimit: 75,
    },
  };
};

const validationSchema = object().shape({
  isSpecialNeedsCertified: boolean().required(),
  disabilitiesList: array().of(string()).min(1, "Please special needs"),
  specialNeedsExperience: string()
    .trim()
    .required("Please describe your special needs experience"),
});

const SpecialNeeds: React.FC<SpecialNeedsProps> = observer(
  ({
    loading,
    onSubmit,
    specialNeedsData = _specialNeedsData,
    specialNeeds,
  }) => {
    React.useEffect(() => scrollToTop(), []);

    const _handleSubmit = async (values: any, formikHelpers: any) => {
      formikHelpers.setSubmitting(true);
      try {
        let res = await onSubmit(values);
        formikHelpers.setSubmitting(false);
        return res;
      } catch (error) {
        formikHelpers.setSubmitting(false);
        // if (error?.spellCheck) {
        //   formikHelpers.setErrors(error.spellCheck);
        //   if (error?.errors) {
        //     setErrorObjects(error.errors);
        //   }
        // }
      }
    };

    return (
      <Formik
        initialValues={{
          disabilitiesList: specialNeeds.disabilitiesList || [],
          isSpecialNeedsCertified:
            specialNeeds.isSpecialNeedsCertified || false,
          specialNeedsExperience: specialNeeds.specialNeedsExperience || "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values, fmk) => _handleSubmit(values, fmk)}
        render={({ handleSubmit, values, errors }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Stack spacing={6} className="special-needs-section">
                <Stack id="form-header-section" mb={4}>
                  <Text
                    fontWeight="bold"
                    lineHeight="shorter"
                    fontSize={["3xl", "4xl"]}
                  >
                    Teaching special needs
                  </Text>
                  <Text color="gray.700">
                    If you're experienced or qualified to teach special needs
                    learners, then complete this form. Otherwise, you may skip
                    this step.
                  </Text>
                </Stack>

                <Field
                  name="isSpecialNeedsCertified"
                  render={({ field, form }: any) => {
                    return (
                      <FormControl>
                        <HStack justifyContent="space-between">
                          <FormLabel
                            htmlFor="show-input"
                            pr={{ base: 5, md: 6 }}
                            width={{ md: "75%" }}
                          >
                            Do you have formal training or certification in
                            Special Needs?
                          </FormLabel>
                          <HStack>
                            <Switch
                              size="lg"
                              defaultChecked={field.value}
                              onChange={({ target }) => {
                                form.setFieldValue(field.name, target.checked);
                              }}
                              id="show-input"
                            />
                          </HStack>
                        </HStack>
                      </FormControl>
                    );
                  }}
                />
                <Field
                  name="disabilitiesList"
                  render={({ field, form }: { field: any; form: any }) => {
                    return (
                      <FormControl
                        isInvalid={
                          commonProps(field, form)["disabilitiesList"].isInvalid
                        }
                      >
                        <FormLabel>Select special need(s)</FormLabel>
                        <CheckboxGroup
                          size="lg"
                          {...field}
                          defaultValue={values.disabilitiesList}
                          onChange={(r) => {
                            form.setFieldValue(field.name, r);
                          }}
                        >
                          <Grid templateColumns="repeat(2, 1fr)" gap={5}>
                            {_specialNeedsData.specialNeeds.map(
                              (option, idx) => {
                                return (
                                  <Checkbox
                                    borderColor="neutral.500"
                                    value={option}
                                    key={option + idx}
                                    // defaultChecked={values.disabilitiesList.includes(option)}
                                  >
                                    <Text fontSize="md">{option}</Text>
                                  </Checkbox>
                                );
                              },
                            )}
                          </Grid>
                        </CheckboxGroup>
                        <FormErrorMessage>
                          {
                            commonProps(field, form)["disabilitiesList"]
                              .errorMessage
                          }
                        </FormErrorMessage>
                      </FormControl>
                    );
                  }}
                />
                <Field
                  name="specialNeedsExperience"
                  render={({ field, form }: any) => {
                    return (
                      <FormControl
                        isInvalid={
                          commonProps(field, form)["specialNeedsExperience"]
                            .isInvalid
                        }
                      >
                        <FormLabel>
                          Describe your special needs experience
                        </FormLabel>
                        <EnhancedTextarea
                          padding="16px"
                          rounded="md"
                          resize="none"
                          minHeight={{ base: "116px", md: "96px" }}
                          borderColor="neutral.500"
                          fontWeight={field.value ? "medium" : "normal"}
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px blue",
                          }}
                          {...field}
                          {...commonProps(field, form)[
                            "specialNeedsExperience"
                          ]}
                          characterCount={field.value ? field.value.length : 0}
                          onChange={(value) => {
                            form.setFieldValue(field.name, value);
                          }}
                        />
                        <FormErrorMessage>
                          {
                            commonProps(field, form)["specialNeedsExperience"]
                              .errorMessage
                          }
                        </FormErrorMessage>
                      </FormControl>
                    );
                  }}
                />

                <Box pt={[4, 5]} width="full">
                  <Button
                    size="lg"
                    isFullWidth
                    type="submit"
                    colorScheme="blue"
                    isLoading={loading}
                    loadingText={specialNeedsData.buttonText.loadingText}
                  >
                    {specialNeedsData.buttonText.saveAndContinue}
                  </Button>
                </Box>
              </Stack>
            </form>
          );
        }}
      />
    );
  },
);

ToggleCheckbox.defaultProps = {
  heading: _specialNeedsData.formTitle.header,
  subHeading: _specialNeedsData.formTitle.subHeader,
  color: "blue",
  placeholder: _specialNeedsData.placeholder,
};

export default SpecialNeeds;
